import React from "react";
import {
    SocialMediaGroup,
    templateActions,
    templateStore,
} from "@pages/Applications/C&C/C&CSetup/templateStore";
import { useSnapshot } from "valtio";
import ReactSwitch from "react-switch";
import { t } from "i18next";
import { logger } from "@helpers/logger";
import LanguageTable from "./LanguageTable";
import { defaultSocialMediaGroup, platformToKey } from "@constants/template";

interface LanguageTableConfig {
    path: string[];
    fields: { key: string; label: string; placeholder?: string }[];
    defaultTexts: Record<string, any>;
    sectionTitle: string;
}

interface ConnectiviteDiversProps {
    path: string[];
    languageTableConfig?: LanguageTableConfig;
}
const ConnectiviteDivers = React.memo(function ConnectiviteDivers({
    path = ["template", "SiteWeb", "Divers", "SocialMedia"],
    languageTableConfig,
}: ConnectiviteDiversProps) {
    const snap = useSnapshot(templateStore);

    const getSocialMediaStateFromStore = (): SocialMediaGroup => {
        try {
            let current = snap as any;
            for (const key of path) {
                if (current[key] === undefined) {
                    return { ...defaultSocialMediaGroup };
                }
                current = current[key];
            }
            return current
                ? JSON.parse(JSON.stringify(current))
                : { ...defaultSocialMediaGroup };
        } catch (error) {
            logger.error("Error getting social media state:", error);
            return { ...defaultSocialMediaGroup };
        }
    };

    const [socialMediaState, setSocialMediaState] = React.useState<
        SocialMediaGroup
    >(getSocialMediaStateFromStore);

    React.useEffect(() => {
        setSocialMediaState(getSocialMediaStateFromStore());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [snap]);

    const timeoutRef = React.useRef<Record<string, NodeJS.Timeout>>({});

    const handleUpdateUrl = React.useCallback(
        (platform: keyof SocialMediaGroup, value: string) => {
            setSocialMediaState((prev) => ({
                ...prev,
                [platform]: {
                    ...prev[platform],
                    Url: value,
                },
            }));

            if (timeoutRef.current[platform]) {
                clearTimeout(timeoutRef.current[platform]);
            }

            timeoutRef.current[platform] = setTimeout(() => {
                templateActions.updateTemplate(
                    [...path, platform, "Url"],
                    value
                );
                delete timeoutRef.current[platform];
            }, 500);
        },
        [path]
    );
    const handleBlur = React.useCallback(
        (platform: keyof SocialMediaGroup) => {
            const value = socialMediaState[platform].Url;

            if (timeoutRef.current[platform]) {
                clearTimeout(timeoutRef.current[platform]);
                delete timeoutRef.current[platform];
            }

            templateActions.updateTemplate([...path, platform, "Url"], value);
        },
        [path, socialMediaState]
    );
    const handleUpdateActivation = React.useCallback(
        (platform: keyof SocialMediaGroup, value: boolean) => {
            setSocialMediaState((prev) => ({
                ...prev,
                [platform]: {
                    ...prev[platform],
                    Activer: value,
                },
            }));
            templateActions.updateTemplate(
                [...path, platform, "Activer"],
                value
            );
        },
        [path]
    );

    React.useEffect(() => {
        const timeouts = timeoutRef.current;
        return () => {
            Object.values(timeouts).forEach((timeout) => {
                clearTimeout(timeout);
            });
        };
    }, []);

    const [isReady, setIsReady] = React.useState(false);

    React.useEffect(() => {
        let pathSocialMedia = [...path];
        if (pathSocialMedia[0] === "template") {
            pathSocialMedia = pathSocialMedia.slice(1);
        }

        if (pathSocialMedia[pathSocialMedia.length - 1] !== "SocialMedia") {
            pathSocialMedia = [...pathSocialMedia, "SocialMedia"];
        }
        const ensureAllPaths = async () => {
            await templateActions.ensurePathExists(pathSocialMedia, {
                ...defaultSocialMediaGroup,
            });

            setIsReady(true);
        };

        ensureAllPaths();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (!isReady) return null;
    return (
        <div className="mt-3  mx-2">
            <h5 style={{ fontWeight: "bold" }}>{t("Connectivity")}</h5>
            {languageTableConfig && (
                <LanguageTable
                    path={languageTableConfig.path}
                    fields={languageTableConfig.fields}
                    defaultTexts={languageTableConfig.defaultTexts}
                    sectionTitle={languageTableConfig.sectionTitle}
                />
            )}
            {Object.entries(socialMediaState).map(([platform, config]) => (
                <div
                    key={platform}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        marginTop: "2%",
                    }}
                >
                    <span
                        style={{
                            pointerEvents: "none",
                            cursor: "default",
                            minWidth: "100px",
                        }}
                        className="ml-3 mr-3"
                    >
                        {platform}
                    </span>

                    <input
                        id={`input-${platform}`}
                        type="text"
                        className="form-control epb_inp_timeAS mr-2"
                        value={config.Url}
                        onChange={(e) =>
                            handleUpdateUrl(
                                platform as keyof SocialMediaGroup,
                                e.target.value
                            )
                        }
                        onBlur={() =>
                            handleBlur(platform as keyof SocialMediaGroup)
                        }
                        placeholder={t(
                            platformToKey[
                                platform as keyof typeof platformToKey
                            ]
                        )}
                    />

                    <ReactSwitch
                        className="cac_icn_quickConnection"
                        offColor="#faafb4"
                        offHandleColor="#E30613"
                        onColor="#c2eec4"
                        onHandleColor="#34C38F"
                        height={19}
                        handleDiameter={24}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        checked={config.Activer}
                        onChange={(checked) =>
                            handleUpdateActivation(
                                platform as keyof SocialMediaGroup,
                                checked
                            )
                        }
                    />
                </div>
            ))}
        </div>
    );
});

export default ConnectiviteDivers;
